import { notEmpty } from "../../../Shared/Utilities/filters";
import { isNullOrWhiteSpace } from "../../../Shared/Utilities/string";

export const initializeAutocomplete = (queryInput: HTMLInputElement) => {
  const formId = queryInput.dataset.autocompleteFormId;

  const targetId = queryInput.dataset.autocompleteTargetId;

  const variant = queryInput.dataset.autocompleteVariant;

  const form = !isNullOrWhiteSpace(formId)
    ? (document.getElementById(formId) as HTMLFormElement | null)
    : undefined;

  const targetContainer = !isNullOrWhiteSpace(targetId)
    ? document.getElementById(targetId)
    : undefined;

  if (form && targetContainer) {
    let selectedIndex: number | undefined;

    const openClassesStr = targetContainer.dataset.openClass ?? "";

    const closedClassesStr = targetContainer.dataset.closedClass ?? "";

    const openClasses = openClassesStr.split(" ").filter(notEmpty);

    const closedClasses = closedClassesStr.split(" ").filter(notEmpty);

    const boxIsOpen = () => queryInput.ariaExpanded === "true";

    const openBox = () => {
      if (!boxIsOpen()) {
        targetContainer.classList.remove(...closedClasses);
        targetContainer.classList.add(...openClasses);

        queryInput.ariaExpanded = "true";
      }
    };

    const closeBox = () => {
      if (boxIsOpen()) {
        targetContainer.classList.remove(...openClasses);
        targetContainer.classList.add(...closedClasses);
        targetContainer.innerHTML = "";

        queryInput.ariaExpanded = "false";

        updateSelectedIndex(undefined);
      }
    };

    const updateSelectedIndex = (newIndex: number | undefined) => {
      if (selectedIndex !== undefined) {
        const activeOption = targetContainer.children[selectedIndex];

        if (activeOption) {
          activeOption.ariaSelected = "false";
        }
      }

      // Wrap around at bounds
      if (newIndex !== undefined) {
        if (newIndex < 0) {
          newIndex = targetContainer.children.length - 1;
        } else if (newIndex >= targetContainer.children.length) {
          newIndex = 0;
        }
      }

      selectedIndex = newIndex;

      if (selectedIndex !== undefined) {
        const activeOption = targetContainer.children[selectedIndex] as
          | HTMLElement
          | undefined;

        if (activeOption) {
          activeOption.ariaSelected = "true";

          queryInput.value = activeOption.dataset.value ?? "";
        }
      }

      queryInput.setAttribute("aria-activedescendant", selectedIndex?.toString() ?? "");
    };

    htmx.on(queryInput, "htmx:confirm", e => {
      // Cancel the "get suggestions" request and close the box if the query value is empty
      if (queryInput.value.length === 0) {
        e.preventDefault();

        closeBox();
      }
    });

    htmx.on(queryInput, "htmx:configRequest", e => {
      const evt = e as ConfigRequestEvent;

      evt.detail.parameters["autocomplete-variant"] = variant;
    });

    htmx.on(queryInput, "htmx:afterOnLoad", e => {
      // Prevent the event to reach the form event handler
      e.stopPropagation();

      if (targetContainer.children.length > 0) {
        openBox();

        for (const option of targetContainer.children) {
          option.addEventListener("click", _ => {
            queryInput.value = (option as HTMLElement).dataset.value ?? "";

            form.requestSubmit();

            closeBox();
          });
        }
      } else {
        closeBox();
      }
    });

    queryInput.addEventListener("blur", _ => {
      // Since the blur event is fired before any option click events, we need to add a small
      // timeout here so they have a chance to run.
      setTimeout(() => closeBox(), 250);
    });

    // Keyboard navigation
    queryInput.addEventListener("keydown", e => {
      if (boxIsOpen()) {
        switch (e.key) {
          case "Enter":
            closeBox();
            break;
          case "Escape":
            closeBox();
            e.preventDefault();
            break;
          case "ArrowUp":
            if (selectedIndex === undefined) {
              updateSelectedIndex(-1);
            } else {
              updateSelectedIndex(selectedIndex - 1);
            }

            e.preventDefault();
            break;
          case "ArrowDown":
            if (selectedIndex === undefined) {
              updateSelectedIndex(0);
            } else {
              updateSelectedIndex(selectedIndex + 1);
            }

            e.preventDefault();
            break;
        }
      }
    });
  } else {
    console.warn("Form or target container missing for autocomplete input", queryInput);
  }
};

document
  .querySelectorAll<HTMLInputElement>("[data-autocomplete]")
  .forEach(initializeAutocomplete);
